import { useCallback } from "react";
import { useDropzone, type FileRejection } from "react-dropzone";
import { UploadCloud } from "lucide-react";
import { showToast } from "@/lib/toast";
import { cn } from "@/lib/utils";

export const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB
export const ACCEPTED_EXTENSIONS = [
  ".pdf",
  ".docx",
  ".xlsx",
  ".txt",
  ".eml",
  ".msg",
];

// Validamos por extensión (no por MIME): .msg y .eml llegan sin MIME
// fiable desde el navegador.
function fileValidator(file: File) {
  // Al arrastrar carpetas, file-selector entrega los ficheros internos;
  // ignoramos entradas sin nombre.
  const name = file.name || "";
  const ext = name.includes(".")
    ? `.${name.split(".").pop()!.toLowerCase()}`
    : "";
  if (!ACCEPTED_EXTENSIONS.includes(ext)) {
    return {
      code: "tipo-no-soportado",
      message: `Tipo de archivo no soportado (${ext || "sin extensión"})`,
    };
  }
  return null;
}

interface DocumentDropzoneProps {
  onFiles: (files: File[]) => void;
  disabled?: boolean;
}

export function DocumentDropzone({ onFiles, disabled }: DocumentDropzoneProps) {
  const onDrop = useCallback(
    (accepted: File[], rejections: FileRejection[]) => {
      for (const rejection of rejections) {
        const tooLarge = rejection.errors.some(
          (e) => e.code === "file-too-large"
        );
        if (tooLarge) {
          showToast.error(
            `«${rejection.file.name}» supera el tamaño máximo de 50 MB`
          );
        } else {
          showToast.error(
            `«${rejection.file.name}»: tipo de archivo no soportado. Se admiten ${ACCEPTED_EXTENSIONS.join(", ")}`
          );
        }
      }
      if (accepted.length > 0) onFiles(accepted);
    },
    [onFiles]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    multiple: true,
    maxSize: MAX_FILE_SIZE,
    validator: fileValidator,
    // false → file-selector recorre directorios arrastrados (webkitGetAsEntry);
    // la File System Access API rompe ese traversal.
    useFsAccessApi: false,
    disabled,
  });

  return (
    <div
      {...getRootProps()}
      className={cn(
        "border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-colors",
        isDragActive
          ? "border-brand bg-brand/5"
          : "border-line-strong hover:border-brand/60 bg-surface",
        disabled && "opacity-50 cursor-not-allowed"
      )}
    >
      <input {...getInputProps()} />
      <UploadCloud
        className={cn(
          "w-10 h-10 mx-auto mb-3",
          isDragActive ? "text-brand" : "text-content-muted/60"
        )}
      />
      <p className="text-sm font-medium text-ink">
        {isDragActive
          ? "Suelta los archivos aquí…"
          : "Arrastra archivos o carpetas, o haz clic para seleccionar"}
      </p>
      <p className="text-xs text-content-muted mt-1">
        PDF, Word, Excel, TXT, EML y MSG · máximo 50 MB por archivo
      </p>
    </div>
  );
}
